<template>
    <footer class="main-footer n-flex n-bg-1 n-flex-s n-border-box" role="contentinfo">
        <div class="c-max-w-sub n-m-auto main-footer-inner">
            <div class="main-footer-item n-flex-g">
                <h2 class="n-f-8 n-f-bold7 n-c-4">{{ $t('footer.letxany') }}</h2>
                <ul>
                    <li class="n-mt-5">
                        <a class="n-f-4 n-c-4 c-a" :href="`mailto:${EMAIL_ADDRESS}`" aria-label="Contact support via email">
                                {{ EMAIL_ADDRESS }}
                            </a>
                    </li>
                    <li class="n-mt-5 main-footer-icons">
                        <a href="https://www.tiktok.com/@sovi_ai0" class="c-hover" target="_blank" rel="noopener noreferrer" aria-label="Follow Sovi.AI on TikTok">
                            <nicon icon="footer-dy" class="n-mr-3 n-c-4" :size="30"></nicon>
                        </a>
    
                        <a href="https://www.reddit.com/r/Sovi_ai/" class="c-hover" target="_blank" rel="noopener noreferrer" aria-label="Follow Sovi.AI on Reddit">
                                <img
                                    src="@/assets/images/reddit.png"
                                    style="width: 25px; height: 25px; object-fit: contain; border-radius: 50%; display: block"
                                    alt="Reddit"
                                    class="n-mr-3" />
                            </a>
    
                        <a href="https://www.instagram.com/sovi_ai_official/" class="c-hover" target="_blank" rel="noopener noreferrer" aria-label="Follow Sovi.AI on Instagram">
                            <nicon icon="footer-camera" class="n-mr-3 n-c-4" :size="30"></nicon>
                        </a>
                    </li>
                    <li class="n-mt-6 main-footer-lang">
                        <language-switcher />
                    </li>
                </ul>
            </div>
    
            <div class="main-footer-item">
                <h2 class="n-f-6 n-f-bold7 n-c-4">{{ $t('footer.features') }}</h2>
                <ul class="main-footer-link-list main-footer-feature-list">
                    <li v-for="link in featureLinks" :key="link.href" class="n-mt-6">
                        <a :href="link.href" class="n-f-3 n-c-4 c-a">
                                {{ $t(link.labelKey) }}
                            </a>
                    </li>
                </ul>
            </div>
    
            <div class="main-footer-item main-footer-item-learn-more">
                <h2 class="n-f-6 n-f-bold7 n-c-4">{{ $t('footer.learnMore') }}</h2>
                <ul class="main-footer-link-list">
                    <li v-for="link in learnMoreLinks" :key="link.href" class="n-mt-6">
                        <a :href="link.href" :target="link.external ? '_blank' : undefined" class="n-f-3 n-c-4 c-a">
                                {{ $t(link.labelKey) }}
                            </a>
                    </li>
                </ul>
            </div>
    
            <div class="main-footer-item">
                <h2 class="n-f-6 n-f-bold7 n-c-4">{{ $t('footer.downloadApp') }}</h2>
                <ul>
                    <li class="n-mt-5">
                        <a :href="appDownloadHref" class="c-hover n-pointer" @click="handleDownloadAppClick">
                                <img
                                    src="@/assets/images/footer/apple-store.png"
                                    class="download-app-btn"
                                    style="width: 140px; height: auto; object-fit: contain"
                                    alt="Download on App Store" />
                            </a>
                    </li>
                </ul>
            </div>
        </div>
    </footer>
</template>

<script setup lang="ts">
import { EMAIL_ADDRESS } from '@/config/constant';
import { siteUrl } from '@/utils/siteUrl';
import { logEvent } from '@/utils/utils.ts';
import { getAppDownloadHref, openAppDownload } from '@/utils/appDownload';
import LanguageSwitcher from '@/components/business/language-switcher.vue';

const appDownloadHref = getAppDownloadHref();

const handleDownloadAppClick = (event: MouseEvent) => {
    logEvent('web_app_download_click');
    openAppDownload(event);
};

const featureLinks = [
    { href: '/chat', labelKey: 'sidebarMenu.asksovi' },
    { href: '/study', labelKey: 'sidebarMenu.aistudy' },
    // { href: '/study?type=knowledge', labelKey: 'sidebarMenu.chatpdf' },
    // { href: '/study?type=quiz', labelKey: 'footer.fileSummariser' },
    { href: '/apexam', labelKey: 'sidebarMenu.aptestprep' },
    { href: '/resources', labelKey: 'sidebarMenu.resources' },
    { href: '/blog', labelKey: 'sidebarMenu.blog' },
    { href: '/search', labelKey: 'sidebarMenu.search' }
]

const learnMoreLinks = [
    { href: '/about', labelKey: 'footer.about', external: false },
    { href: '/faq', labelKey: 'footer.faq', external: false },
    { href: siteUrl('/apps/career/'), labelKey: 'footer.careers', external: true },
    { href: '/privacy-policy', labelKey: 'svipUpgrade.privacy', external: true },
    { href: '/terms-of-service', labelKey: 'svipUpgrade.terms', external: true }
]
</script>

<style scoped lang="scss">
.main-footer {
    .main-footer-inner {
        padding-bottom: 0;
        display: flex;
        justify-content: space-around;
        flex-wrap: wrap;
        gap: 20px;
        position: relative;
        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 30px;
            right: 30px;
            height: 1px;
            transform: scaleY(0.5);
            background-color: #8e8e93;
        }
    }
    .main-footer-item {
        padding-bottom: 60px;
        padding-top: 40px;
        &:last-child {
            margin-left: 160px;
            padding-bottom: 90px;
        }
    }
    .main-footer-link-list {
        margin: 0;
        padding: 0;
        list-style: none;
    }
    .main-footer-feature-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(118px, 1fr));
        gap: 12px 22px;
        margin-top: 24px;

        li {
            margin-top: 0 !important;
        }

        a {
            white-space: nowrap;
            line-height: 1.35;
        }
    }
    .main-footer-icons {
        display: flex;
        align-items: center;
    }
    .download-app-btn {
        width: 140px;
        height: auto;
        object-fit: contain;
    }
}

@media screen and (max-width: 768px) {
    .main-footer {
        background: #ffffff;

        .main-footer-inner {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 0 !important;
            padding: 8px 18px 108px;

            &:before {
                left: 18px;
                right: 18px;
                background-color: rgba(15, 23, 42, 0.1);
            }
        }

        .main-footer-item {
            width: 100%;
            display: flex;
            align-items: flex-start;
            flex-direction: column;
            border-bottom: 1px solid rgba(15, 23, 42, 0.08);
            padding: 22px 0;
            box-sizing: border-box;

            &:first-child h2 {
                font-size: 20px;
                line-height: 1.2;
                white-space: nowrap;
            }

            &:nth-child(2),
            &:nth-child(3) {
                width: 50%;
                padding-right: 12px;
            }

            &:last-child {
                margin-left: 0;
                padding-bottom: 22px;
                border-bottom: none;
            }

            h2 {
                margin-bottom: 4px;
                color: #0f172a;
                font-size: 16px;
                line-height: 1.25;
            }

            li {
                margin-top: 10px !important;
                padding-left: 0;
                text-align: left;
            }

            .main-footer-feature-list {
                grid-template-columns: 1fr;
                gap: 0;
                margin-top: 6px;

                li {
                    margin-top: 0 !important;
                }
            }

            a {
                color: #475569;
                font-size: 13px;
                line-height: 1.35;
                display: inline-flex;
                align-items: center;
                min-height: 40px;
                touch-action: manipulation;
                -webkit-tap-highlight-color: transparent;
                transition: color 0.16s ease;

                &:active {
                    color: #6d35db;
                }
            }

            .main-footer-icons {
                justify-content: flex-start;
            }

            .download-app-btn {
                width: 132px !important;
            }
        }
    }
}
</style>
